import { FacetsManager } from "./FacetsManager";
import { DtViewerState } from "../DtViewerState";
import { FacetTypes, UNASSIGNED } from "./Facets";
import { getFloorBoxFuzzy } from "./floor-footprint";
import { Label3D } from "../../gui/Label3D";
import { ANIM_ENDED } from "../../application/EventTypes";
import i18n from "i18next";
import { sortedArrayHas } from "./sortedArray";

const av = Autodesk.Viewing;
const ave = av.Extensions;

// Taken from ClusterGizmo, default flyTime == layout animation's default
function flyToView(facility, bboxes, flyTime = 2.0) {
  // Sum bboxes
  let bbox;

  bboxes.forEach((b) => {
    if (!bbox) {
      bbox = b.clone();
    } else {
      bbox.union(b);
    }
  });

  if (!bbox || bbox.isEmpty()) {
    return;
  }

  // Set camera position/distance to center of clusters
  const center = bbox.getCenter();
  const size = bbox.size();
  const dist = Math.max(size.x, size.y, size.z);

  const dstView = facility.viewer.impl.camera.clone();
  dstView.target.copy(center);

  // Set camera angle to top down view
  const viewDir = { x: 0.5, y: -0.5, z: 0.5 };
  dstView.position.set(center.x + viewDir.x * dist, center.y + viewDir.y * dist, center.z + viewDir.z * dist);

  DtViewerState.flyToView(facility, { camera: dstView }, flyTime);
}

// Taken from VisualClusters
function createSceneLayout(models, clusters, options) {
  // Make sure that we only work on supported models
  models = models.filter((m) => m.is3d() && Boolean(m.getInstanceTree()));

  const sceneBox = new THREE.Box3();
  for (let i = 0; i < models.length; i++) {
    const modelBox = models[i].getBoundingBox();
    sceneBox.union(modelBox);
  }

  // Exclude topography, rooms and empty clusters
  const filter = (c) => c.name !== 'Revit Topography' && c.name !== 'Revit Rooms' && c.name !== 'Revit <Sketch>' && c.shapes.length !== 0;
  clusters = clusters.filter(filter);

  // Compute layouts
  return ave.VisualClusters.createClusterSetLayout(clusters, models, sceneBox, options);
}

/**
 * @private
 */
FacetsManager.prototype.clusterGeneric = function (ext, models, clusteredFacetId, fitClustersToView, skipAnimate) {
  // TODO: apply clustering to elements from other scopes, but cluster them as un-assigned/defined/classified.
  // Tickets / Alerts should follow their host as they do not have independent positioning.
  const facetScope = this.getContext();

  const modelMap = {};
  models.forEach((m) => {
    modelMap[m.urn()] = m;
  });

  const targetFacet = facetScope.getFacet(clusteredFacetId);
  const idsInMultipleFacets = facetScope.idsInMultipleFacets[facetScope.getFacetDefIndex(clusteredFacetId)];

  // Create clusters map to dedup facets across models
  let multipleElementsClustername = i18n.t('Multiple');
  if (clusteredFacetId === FacetTypes.mepSystems) {
    multipleElementsClustername = i18n.t('Multiple Systems');
  } else if (clusteredFacetId === FacetTypes.spaces) {
    multipleElementsClustername = i18n.t('Multiple Rooms');
  }

  const Cluster = ave.VisualClusters.Cluster;
  const Shape = ave.VisualClusters.Shape;

  const clusterMap = {};

  const tmpFloat6 = new Float32Array(6);
  const box = new THREE.Box3();
  const size = new THREE.Vector3();

  function getShape(instanceTree, modelId, dbId) {
    // get box as 6 floats in tmpArray
    instanceTree.getNodeBox(dbId, tmpFloat6);

    if (tmpFloat6[0] === 0 && tmpFloat6[1] === 0 && tmpFloat6[2] === 0 &&
    tmpFloat6[3] === 0 && tmpFloat6[4] === 0 && tmpFloat6[5] === 0) {
      return;
    }

    // convert to Box3
    box.min.set(tmpFloat6[0], tmpFloat6[1], tmpFloat6[2]);
    box.max.set(tmpFloat6[3], tmpFloat6[4], tmpFloat6[5]);

    // For empty boxes, the diagonal contains infinity, which isn't helpful for layouting.
    // So, we return nothing in this case (makes calling code skip / ignore the element).
    let shape;
    if (box.isEmpty()) {
      return;
    } else {
      box.getSize(size);
      shape = new Shape(modelId, dbId, size);

      // If an element's bbox is larger in x than in y direction, we swap the sizes and mark the element for
      // rotation. These elements will get rotated by 90 degrees in the cluster for better alignment.
      if (size.x > size.y) {
        shape.sizeX = size.y;
        shape.sizeY = size.x;
        shape.isRotated = true;
      }

      return shape;
    }
  }

  /**
   * @param {FacetNode} f
   */
  function clusterFacet(f) {
    const facetName = (f.label || f.id)?.toString();
    if (facetName === undefined) {
      console.log(`No cluster name found. Skipping facet.`);
      return;
    }

    for (const [model, ids] of f.ids) {
      const modelId = model.id;
      const isolatedNodes = model.visibilityManager.isolatedNodes;
      const noFilters = isolatedNodes.size === 0;
      const instanceTree = model.getInstanceTree();
      const multipleFacets = idsInMultipleFacets?.get(model);

      for (const dbId of ids) {
        if (noFilters || isolatedNodes.has(dbId)) {
          const assignedToMultiple = multipleFacets && sortedArrayHas(multipleFacets, dbId);
          if (assignedToMultiple) {
            continue;
          }

          const cluster = clusterMap[facetName] || (clusterMap[facetName] = new Cluster(facetName));

          const shape = getShape(instanceTree, modelId, dbId);

          if (!shape) {
            continue;
          }

          cluster.shapes.push(shape);

          // Track extend of each cluster when aligning all shapes in a row.
          const rowSize = cluster.rowSize;
          rowSize.x += shape.sizeX;
          // Track y-extent of row (determined by largest shape along y)
          rowSize.y = Math.max(rowSize.y, shape.sizeY);
          // Also track size of the cluster when stacking all shapes on top of each other.
          rowSize.z += shape.sizeZ;
        }
      }
    }

    // Recursively cluster children in case of hierarchical facets
    if (f.children) {
      for (const child of f.children) {
        clusterFacet(child);
      }
    }
  }

  function clusterMultiFacet() {
    if (!idsInMultipleFacets) {
      return;
    }

    const cluster = new Cluster(multipleElementsClustername);
    clusterMap[multipleElementsClustername] = cluster;

    for (const [model, ids] of idsInMultipleFacets) {
      const modelId = model.id;
      const instanceTree = model.getInstanceTree();
      const isolatedNodes = model.visibilityManager.isolatedNodes;
      const noFilters = isolatedNodes.size === 0;

      for (const dbId of ids) {
        if (noFilters || isolatedNodes.has(dbId)) {
          const shape = getShape(instanceTree, modelId, dbId);

          if (!shape) {
            continue;
          }

          cluster.shapes.push(shape);

          // Track extend of each cluster when aligning all shapes in a row.
          const rowSize = cluster.rowSize;
          rowSize.x += shape.sizeX;
          // Track y-extent of row (determined by largest shape along y)
          rowSize.y = Math.max(rowSize.y, shape.sizeY);
          // Also track size of the cluster when stacking all shapes on top of each other.
          rowSize.z += shape.sizeZ;
        }
      }
    }
  }

  // Cluster the facet.
  let t0 = performance.now();
  clusterMultiFacet();
  targetFacet?.forEach(clusterFacet);

  // Finalize clusters
  const options = ave.VisualClusters.getDefaultClusterOptions();
  const clusters = Object.values(clusterMap);
  for (const cluster of clusters) {
    // Consider spacing in the cluster size
    const spacing = (cluster.shapes.length - 1) * options.spacing;
    cluster.rowSize.x += spacing;
    cluster.rowSize.z += spacing;
  }
  let t1 = performance.now();
  // console.log('  Clustering facet took', t1 - t0);

  t0 = performance.now();
  const customLayout = createSceneLayout(models, clusters, options);
  t1 = performance.now();
  // console.log('  Creating scene layout took', t1 - t0);

  t0 = performance.now();
  ext.setClusterLayout(customLayout, true);
  ext.setLayoutActive(true, skipAnimate);
  t1 = performance.now();
  // console.log('  Applying layout (excl. animation) took', t1 - t0);

  if (fitClustersToView && customLayout.clusterLayouts?.length) {
    const bboxes = customLayout.clusterLayouts.map((layout) => layout.getBBox());
    flyToView(this.facility, bboxes);
  }

  return customLayout;
};

/**
 * @private
 */
FacetsManager.prototype.clusterFloor = function (ext, fitClustersToView, skipLabels, skipAnimate) {
  // TODO: apply clustering to elements from other scopes, but cluster them as un-assigned/defined/classified.
  // Tickets / Alerts should follow their host as they do not have independent positioning.
  const facetScope = this.getContext();

  let t0, t1;
  t0 = performance.now();
  const levelsFacetDef = facetScope.getFacetDef(FacetTypes.levels);
  const facetFilter = levelsFacetDef.filter;

  const levelsFacet = facetScope.getFacet(FacetTypes.levels);

  const hasFacetFilter = facetFilter.size > 0;
  const filteredFacet = levelsFacet.filter((f) => facetFilter.has(f.id));
  if (hasFacetFilter && filteredFacet.length === 0) {
    console.warn(`Facet filter for "${levelsFacetDef.id}" facet is probably outdated`, facetFilter);
  }

  const floorFacet = hasFacetFilter ? filteredFacet : levelsFacet;

  let floorBoxes = {};
  const models = new Set();

  let area = 0;

  //Calculate horizontal floor areas
  for (let i = 0; i < floorFacet.length; i++) {
    let floor = floorFacet[i];

    let fbox = getFloorBoxFuzzy(this.facility, floor);

    if (!fbox) {
      continue;
    }

    let xSpan = fbox.max.x - fbox.min.x;
    let ySpan = fbox.max.y - fbox.min.y;
    area += xSpan * ySpan;

    floorBoxes[floor.id] = {
      box: fbox,
      facet: floor
    };

    for (const [model] of floor.ids) {
      models.add(model);
    }
  }

  //Sort floors by elevation, we will distribute them
  //left to right and bittom to top starting with the lowest.
  let floorBoxesList = Object.values(floorBoxes).slice();
  floorBoxesList.sort((a, b) => {

    if (a.facet.id === UNASSIGNED) return -1;

    if (b.facet.id === UNASSIGNED) return 1;

    return a.box.min.z - b.box.min.z;
  });

  let rowWidthHalf = Math.sqrt(area) * 0.5;

  //Calculate move distance for each floor's geometries
  let curX = -rowWidthHalf;
  let curY = -rowWidthHalf;
  let maxHeight = -1;

  for (let i = 0; i < floorBoxesList.length; i++) {

    let fbox = floorBoxesList[i];
    let box = fbox.box; // fbox.roomBox;

    if (!box.isEmpty()) {
      // Align min corner to (curX, curY)
      fbox.move = new THREE.Vector3(curX - box.min.x, curY - box.min.y, -box.min.z);
    } else {
      fbox.move = new THREE.Vector3(10000, 10000, 10000);
    }

    fbox.movedBox = fbox.box.clone();
    fbox.movedBox.min.add(fbox.move);
    fbox.movedBox.max.add(fbox.move);

    let h = box.max.y - box.min.y;
    if (h > maxHeight) {
      maxHeight = h;
    }

    if (curX > rowWidthHalf) {
      curY += maxHeight + 0.1 * rowWidthHalf;
      curX = -rowWidthHalf;
      maxHeight = -1;
    } else {
      curX += box.max.x - box.min.x + 0.1 * rowWidthHalf;
    }
  }

  t1 = performance.now();
  // console.log('  Clustering levels facet took', t1 - t0);
  t0 = performance.now();

  //Set up the clustering animation "manually"
  let vc = Autodesk.Viewing.Extensions.VisualClusters;

  let sceneAnimState, clusterSetLayout;
  const gpuAnimationSupported = ext.isGPUAnimationSupported();
  if (gpuAnimationSupported) {
    clusterSetLayout = new vc.ClusterSetLayout(Array.from(models));
  } else {
    sceneAnimState = new vc.SceneAnimState(this.facility.facetsManager.models);
  }

  const createAnimStates = (facetNode, fbox) => {
    for (let [model, ids] of facetNode.ids) {
      for (let dbId of ids) {
        let as = new vc.ObjectAnimState(dbId);
        as.move.copy(fbox.move);
        sceneAnimState.setAnimState(model.id, dbId, as);
      }
    }
  };

  const createClusterLayout = (facetNode, fbox) => {
    const shapeBoxes = new vc.ShapeBoxes(Array.from(models));

    const tmpFloat6 = new Float32Array(6);
    const box = new THREE.Box3();
    const size = new THREE.Vector3();

    const cluster = new vc.Cluster(facetNode.label || facetNode.id);
    const shapes = cluster.shapes;
    const clusterLayout = new vc.ClusterLayout(cluster, shapeBoxes);
    clusterLayout.position.copy(fbox.move);
    const positions = [];
    let offset = 0;

    for (const [model, ids] of facetNode.ids) {
      const modelId = model.id;
      const instanceTree = model.getInstanceTree();

      for (const dbId of ids) {
        // get box as 6 floats in tmpArray
        instanceTree.getNodeBox(dbId, tmpFloat6);

        // convert to Box3
        box.min.set(tmpFloat6[0], tmpFloat6[1], tmpFloat6[2]);
        box.max.set(tmpFloat6[3], tmpFloat6[4], tmpFloat6[5]);

        if (box.isEmpty()) {
          // Skip elements with an empty box.
          continue;
        }

        box.getSize(size);
        const shape = new vc.Shape(modelId, dbId, size);
        shapes.push(shape);

        positions[offset++] = box.min.x;
        positions[offset++] = box.min.y;
        positions[offset++] = box.min.z;
      }
    }

    clusterLayout.positions = Float32Array.from(positions);
    clusterSetLayout.clusterLayouts.push(clusterLayout);
  };

  this.labels = [];

  for (let facetNode of floorFacet) {

    let fbox = floorBoxes[facetNode.id];

    if (!fbox?.move) {
      continue;
    }

    if (gpuAnimationSupported) {
      createClusterLayout(facetNode, fbox);
    } else {
      createAnimStates(facetNode, fbox);
    }

    if (skipLabels) {
      continue;
    }

    let labelPos = fbox.movedBox.getCenter();
    labelPos.z = fbox.movedBox.min.z;

    const label = new Label3D(this.viewer, labelPos, facetNode.label || facetNode.id);
    label.removeLabelListeners();
    label.setVisible(false);
    this.labels.push(label);

    // Hide label if ClusterGizmo size on screen is below MinPixels threshold.
    const MinPixels = 50;
    label.setWorldBox(fbox.movedBox, MinPixels);

    label.container.style.pointerEvents = 'auto';

    const fitToCluster = () => this.viewer.navigation.fitBounds(false, fbox.movedBox);
    label.container.addEventListener('click', fitToCluster);
  }

  t1 = performance.now();
  // console.log('  Creating scene layout took', t1 - t0);
  t0 = performance.now();

  let onAnimEnd = () => {
    this.labels?.forEach((label) => {
      label.setVisible(true);
    });
  };

  if (!skipAnimate) {
    this.viewer.addEventListener(ANIM_ENDED, onAnimEnd, { once: true });
  }

  if (gpuAnimationSupported) {
    ext.setClusterLayout(clusterSetLayout);
  } else {
    // Make sure there's no cluster layout, otherwise VC will generate a sceneAnimState
    ext.setClusterLayout(null);
    ext.setSceneAnimState(sceneAnimState);
  }
  ext.setLayoutActive(true, skipAnimate);

  t1 = performance.now();
  // console.log('  Applying layout (excl. animation) took', t1 - t0);

  if (fitClustersToView) {
    let newBoxes = [];
    for (let fbox of floorBoxesList) {
      let box = fbox.box;
      box.min.add(fbox.move);
      box.max.add(fbox.move);
      newBoxes.push(box);
    }

    flyToView(this.facility, newBoxes);
  }

  if (skipAnimate) {
    onAnimEnd();
  }

  return floorBoxes;
};

/**
 * @param {Boolean} fitClustersToView
 * @param {Boolean|true} updateScene Add listener to indicate elements' positions may have changed
 * @param {Boolean} skipAnimate Whether to skip the animation the layout of the visual clusters.
 */
FacetsManager.prototype.clearClusters = function (fitClustersToView, updateScene = true, skipAnimate, unsetLayout) {
  const viewer = this.viewer;
  const ext = viewer.getExtension('Autodesk.VisualClusters');

  if (!ext) {
    // Extension may not be loaded before cleanup
    return;
  }

  // Cleanup custom floor labels
  this.labels?.forEach((l) => l.dispose());

  const onAnimEnd = () => {
    this.facetsEffects.enableHighlighting(true);
    this.facetsEffects.invalidateEffectCaches();
    this.facetsEffects.clearFacetHighlight();
    this.highlightLocations();
    // Regenerate the ground shadow & updates ghosting positions
    viewer.impl.sceneUpdated();
  };

  if (updateScene && !skipAnimate) {
    viewer.addEventListener(ANIM_ENDED, onAnimEnd, { once: true });
  }

  if (unsetLayout) {
    // Going back to original state.
    ext.setLayoutActive(false, skipAnimate);
    // Unset layout on the extension to avoid memory leak.
    ext.setClusterLayout(null);
    ext.setSceneAnimState(null);
  } else {
    // Transition between two layouts.
    ext.prepareLayoutTransition();
  }

  // fit view to active (visible) models
  const models = this.viewer.getVisibleModels();
  if (fitClustersToView && models.length) {
    const bboxes = [];
    for (const model of models) {
      const box = model.getBoundingBox();
      if (box) {
        bboxes.push(new THREE.Box3().copy(box));
      }
    }
    flyToView(this.facility, bboxes);
  }

  this.facetsEffects.enableHighlighting(false);

  if (updateScene && skipAnimate) {
    onAnimEnd();
  }
};

/**
 * @param {String} clusteredFacetId
 * @param {Boolean} fitClustersToView
 * @param {Boolean} skipLabels - Option to skip display of clickable labels (supported for floor clustering only)
 * @param {Boolean} skipAnimate Whether to skip the animation the layout of the visual clusters.
 */
FacetsManager.prototype.updateClusters = async function (clusteredFacetId, fitClustersToView, skipLabels, skipAnimate) {

  // console.log('Updating cluster...');
  const start = performance.now();

  const scene = this.facility;
  const ext = scene.viewer.getExtension('Autodesk.VisualClusters');

  const models = scene.viewer.getVisibleModels();

  const doClustering = models.length && clusteredFacetId;

  if (!models.length || this.currentClusterFacet) {
    // Defer scene update until clustering is done
    this.clearClusters(!doClustering && fitClustersToView, !doClustering, skipAnimate, !doClustering);
  }

  let res;

  if (doClustering) {
    // Clustering requires bounding boxes (and fragments) to be loaded.
    await waitForAllIsolatedFragments(models);

    const t0 = performance.now();
    for (const model of models) {
      const nodeAccess = model.getInstanceTree().nodeAccess;
      if (nodeAccess.nodeBoxes) {
        continue;
      }
      const fragBoxes = model.getFragmentList().boxes;
      nodeAccess.computeBoxes(fragBoxes);
    }
    const t1 = performance.now();
    // console.log('  Calculating node boxes took', t1 - t0);

    const onAnimEnd = () => {
      this.facetsEffects.enableHighlighting(true);
      this.facetsEffects.invalidateEffectCaches();
      // Clear again in case user re-enabled clustering while animating
      this.facetsEffects.clearFacetHighlight();
      this.facetsEffects.clearSpaceOverlays();
      // Regenerate the ground shadow & updates ghosting positions
      scene.viewer.impl.sceneUpdated();
    };

    if (!skipAnimate) {
      scene.viewer.addEventListener(ANIM_ENDED, onAnimEnd, { once: true });
    }

    this.facetsEffects.enableHighlighting(false);

    // Clear rooms overlay as it is not supposed to be used together
    this.facetsEffects.clearSpaceOverlays();

    if (clusteredFacetId === FacetTypes.levels) {
      res = this.clusterFloor(ext, fitClustersToView, skipLabels, skipAnimate);
    } else {
      res = this.clusterGeneric(ext, models, clusteredFacetId, fitClustersToView, skipAnimate);
    }

    if (skipAnimate) {
      onAnimEnd();
    }
  }

  this.currentClusterFacet = clusteredFacetId;

  const end = performance.now();
  // console.log('Updating cluster took', end - start);

  return res;
};

/** Awaits fragments for all isolated elements for given models. */
async function waitForAllIsolatedFragments(models) {
  const thenAll = [];
  for (const { loader, visibilityManager } of models) {
    if (!loader || !visibilityManager) continue;

    const waitForFrags = loader.loadFragmentsForElements([...visibilityManager.isolatedNodes]);
    thenAll.push(waitForFrags);
  }
  await Promise.all(thenAll);
}